import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import parseISO from 'date-fns/parseISO';
import subDays from 'date-fns/subDays';
import isAfter from 'date-fns/isAfter';
import { useTranslation } from 'react-i18next';

import { Link, Button } from 'formlabs-web-components/Link';
import { Spinner } from 'formlabs-web-components/Navigation';
import AddToCart from '../AddToCart';
import Amount from '../Amount';
import CountryContext from '../../../context/country';
import LeadTime from '../LeadTime';
import Price from '../Price';

import productLoadingAnimation from '../../../../../public/product-loading.svg';

import './Product.module.css';

const cdnUrl = `${
  process.env.RAZZLE_MAGENTO_MEDIA_URL
}/catalog/product/400x400`;

const isNewFrom = subDays(new Date(), 90);

const PillWrapper = ({ children, type }) => (
  <div styleName={classNames('pill', type)}>{children}</div>
);

const Pill = ({ product }) => {
  const { t } = useTranslation();
  const { attributes, created_at, sku } = product;
  const { lead_time } = attributes;

  const hasLeadTime = lead_time && lead_time !== '0';
  if (hasLeadTime) {
    return (
      <PillWrapper type="leadtime">
        <LeadTime value={lead_time} sku={sku} />
      </PillWrapper>
    );
  }

  const createdAt = parseISO(created_at);
  const isNew = isAfter(createdAt, isNewFrom);
  if (isNew) {
    return <PillWrapper type="new">{t('new')}</PillWrapper>;
  }

  return null;
};

const AddButton = ({ loading, success, ...props }) => (
  <button styleName={classNames('add', { success })} {...props}>
    {loading ? (
      <Spinner size={3} color="#0762c8" />
    ) : success ? (
      <i styleName="success-icon" className="fl-check" />
    ) : (
      <i className="fl-add_shopping_cart" />
    )}
  </button>
);

const Product = ({ product = {}, displayStyle = 'grid', empty = false }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(0);
  const [success, setSuccess] = useState(false);
  const { storePage } = useContext(CountryContext);

  useEffect(
    () => {
      if (success) {
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      }
    },
    [success]
  );

  if (empty) {
    return (
      <div
        styleName={classNames('product', 'empty', displayStyle)}
        style={{ backgroundImage: `url(${productLoadingAnimation})` }}
      />
    );
  }

  const { attributes, name, price, sku, url: productPath } = product;
  const { image, has_options } = attributes;

  const url = `${storePage}${productPath}/`;
  const isBundle = has_options !== '0';

  const handleAmountChange = qty => {
    setAmount(qty);
  };

  const onSuccess = () => {
    setSuccess(true);
  };

  return (
    <div styleName={classNames('product', displayStyle)}>
      <Link to={url}>
        <img src={`${cdnUrl}${image}`} alt={name} loading="lazy" />
      </Link>
      <div styleName="body">
        <div styleName="name">
          <Link to={url}>{name}</Link> <Pill product={product} />
        </div>
        {displayStyle === 'list' && <div styleName="sku">{sku}</div>}
      </div>
      <div styleName="price">
        <Price price={price} />
      </div>

      {displayStyle === 'list' && (
        <div styleName="actions">
          {isBundle ? (
            <Button to={url} color="white-blue-text">
              {' '}
              {t('View Product')}{' '}
            </Button>
          ) : (
            <>
              <div styleName="amount">
                <Amount onAmountChange={handleAmountChange} />
              </div>
              <AddToCart
                disableModal
                amount={amount}
                sku={sku}
                name={name}
                onSuccess={onSuccess}
              >
                <AddButton success={success} />
              </AddToCart>
            </>
          )}
        </div>
      )}

      {displayStyle === 'grid' && !isBundle && (
        <div styleName={classNames('actions', { active: success })}>
          <AddToCart
            disableModal
            amount={1}
            sku={sku}
            name={name}
            onSuccess={onSuccess}
          >
            <AddButton success={success} />
          </AddToCart>
        </div>
      )}
    </div>
  );
};

export default Product;
